/**
 * PATH: packages/email-hub/src/feature/reader-header/placements/placementModel.ts
 * FILE_ID: EMAILHUB-READERHEADER-PLACEMENTS-MODEL-001
 * PURPOSE: Pure row/view derivations for the Placement Manager — source badges,
 *   availability (ok/locked/stale), version tokens, search filter, and the
 *   defensive readers for fields the backend only sometimes emits.
 * ROLE IN ARCHITECTURE: Pure I→O layer between EmailAllocationSummary payloads
 *   and the manager rows. No React, no network.
 * MAIN EXPORTS: PLACEMENT_FILTER_THRESHOLD, placementSourceBadge,
 *   placementAvailability, placementVersionToken, filterPlacements,
 *   placementExtras, snapshotPlacementTokens, findChangedPlacements.
 * NON-RESPONSIBILITIES: Primary selection (model/placements.ts), persistence
 *   (primaryOverride.ts), mutations (kanban-host hooks).
 * NOTES FOR FUTURE AI: `placementExtras` reads OPTIONAL fields (visibility,
 *   creator, updated_at) defensively because the detail serializer does not
 *   emit them yet — discovered scope for a backend follow-up. Never make these
 *   required on EmailAllocationSummary; older cached payloads lack them.
 */
import type { EmailAllocationSummary } from '@quotech/tenant-shared/email/api'

/** Above this many placements the manager grows a search/filter field. */
export const PLACEMENT_FILTER_THRESHOLD = 5

export type PlacementAvailability = 'ok' | 'locked' | 'stale'

export interface PlacementSourceBadge {
  label: 'Manual' | 'Rule' | 'AI' | 'AI pending'
  /** Presentational tone token consumed by the row. */
  tone: 'manual' | 'rule' | 'ai' | 'pending'
}

/** Map an allocation source onto the plan's manual/rule/AI badge vocabulary. */
export function placementSourceBadge(
  source: EmailAllocationSummary['source'],
): PlacementSourceBadge {
  switch (source) {
    case 'USER_MANUAL':
      return { label: 'Manual', tone: 'manual' }
    case 'RULE':
      return { label: 'Rule', tone: 'rule' }
    case 'AI_SUGGESTED_PENDING':
      return { label: 'AI pending', tone: 'pending' }
    case 'AI_AUTO':
    case 'AI_SUGGESTED_USER_CONFIRMED':
    default:
      return { label: 'AI', tone: 'ai' }
  }
}

/**
 * Classify a placement row's availability.
 *
 * Inputs: one allocation summary. Output:
 * - 'locked' — the RBAC gate nulled the display names (private board/card the
 *   viewer cannot read). Ids exist, names do not: render Locked, leak nothing.
 * - 'stale'  — the referenced card was archived/deleted after allocation:
 *   render unavailable + "Remove stale placement".
 * - 'ok'     — a normal interactive row.
 * Failure mode: unknown card_status values are treated as 'ok' (fail open for
 * display only; every mutation still validates server-side).
 */
export function placementAvailability(
  summary: EmailAllocationSummary,
): PlacementAvailability {
  if (!summary.board_name && !summary.lane_name) return 'locked'
  if (summary.card_status === 'ARCHIVED') return 'stale'
  return 'ok'
}

/**
 * Cheap concurrency token for "changed elsewhere" detection. The detail
 * payload carries no per-allocation updated_at yet, so the token folds the
 * fields any concurrent move/accept/revoke would change.
 */
export function placementVersionToken(summary: EmailAllocationSummary): string {
  return [
    summary.allocation_id,
    summary.board_id,
    summary.lane_id,
    summary.card_status ?? '',
    summary.source,
    summary.needs_review ? '1' : '0',
  ].join(':')
}

/** allocation_id → token map, captured when the manager opens. */
export function snapshotPlacementTokens(
  placements: readonly EmailAllocationSummary[],
): ReadonlyMap<string, string> {
  return new Map(
    placements.map((p) => [p.allocation_id, placementVersionToken(p)]),
  )
}

/**
 * Ids whose token changed since the snapshot (concurrent edit in another
 * tab/session). New placements (absent from the snapshot) are NOT flagged —
 * appearing is additive, not a conflicting overwrite.
 */
export function findChangedPlacements(
  snapshot: ReadonlyMap<string, string>,
  placements: readonly EmailAllocationSummary[],
): ReadonlySet<string> {
  const changed = new Set<string>()
  for (const placement of placements) {
    const prior = snapshot.get(placement.allocation_id)
    if (prior !== undefined && prior !== placementVersionToken(placement)) {
      changed.add(placement.allocation_id)
    }
  }
  return changed
}

/**
 * Cross-organisation move gate (plan: cross-org move warns about revocation
 * and requires explicit confirm). Returns true only when BOTH org ids are
 * known and differ — unknown targets fail open here because the board
 * pickers are already org-scoped fail-closed (BUG-2026-07-15), which makes a
 * cross-org target structurally unreachable today; this guard is the seam a
 * future host with cross-org targets must call before moving.
 */
export function moveRequiresCrossOrgConfirm(
  currentOrgId: number | null | undefined,
  targetOrgId: number | null | undefined,
): boolean {
  return (
    currentOrgId != null && targetOrgId != null && currentOrgId !== targetOrgId
  )
}

/** Optional fields the backend may add later — read defensively, never trusted. */
export interface PlacementExtras {
  visibility: string | null
  creatorName: string | null
  /** ISO timestamp of the last change, when the payload carries one. */
  updatedAt: string | null
}

export function placementExtras(
  summary: EmailAllocationSummary,
): PlacementExtras {
  const raw = summary as unknown as Record<string, unknown>
  const str = (key: string): string | null =>
    typeof raw[key] === 'string' && (raw[key] as string).trim() !== ''
      ? (raw[key] as string)
      : null
  return {
    visibility: str('visibility'),
    creatorName: str('created_by_name') ?? str('creator_name'),
    updatedAt: str('updated_at') ?? str('created_at'),
  }
}

/**
 * One text query across board, lane, review status, source badge, and the
 * organisation name (all placements share the email's home organisation
 * today). Empty query returns the list unchanged. Locked rows only match on
 * status ('locked') so private names can never be discovered by probing.
 */
export function filterPlacements(
  placements: readonly EmailAllocationSummary[],
  query: string,
  organisationName?: string | null,
): EmailAllocationSummary[] {
  const needle = query.trim().toLowerCase()
  if (!needle) return [...placements]
  const orgText = (organisationName ?? '').toLowerCase()
  return placements.filter((placement) => {
    const availability = placementAvailability(placement)
    if (availability === 'locked') return 'locked'.includes(needle)
    const haystack = [
      placement.board_name ?? '',
      placement.lane_name ?? '',
      placement.card_status ?? '',
      placementSourceBadge(placement.source).label,
      placement.needs_review ? 'review pending' : '',
      availability === 'stale' ? 'stale unavailable' : '',
      orgText,
    ]
      .join(' ')
      .toLowerCase()
    return haystack.includes(needle)
  })
}
